import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CssBaseline, IconButton, ThemeProvider, Tooltip, type PaletteMode, type SxProps, type Theme } from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import { createAppTheme, tokens } from './theme';

const STORAGE_KEY = 'portal.theme';

function initialMode(): PaletteMode {
  // index.html sets data-theme before first paint; read it back so React agrees.
  const attr = document.documentElement.dataset.theme;
  if (attr === 'dark' || attr === 'light') return attr;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const ColorModeContext = createContext<{ mode: PaletteMode; toggle: () => void }>({ mode: 'light', toggle: () => {} });

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(initialMode);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', mode === 'dark' ? '#101828' : '#ffffff');
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage unavailable: the choice lasts for this tab only */
      }
      return next;
    });
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export const useColorMode = () => useContext(ColorModeContext);

/** Round light/dark switch used in the header and on the auth screens. */
export function ThemeToggle({ sx, floating }: { sx?: SxProps<Theme>; floating?: boolean }) {
  const { mode, toggle } = useColorMode();
  const dark = mode === 'dark';
  return (
    <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton
        onClick={toggle}
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        sx={[
          floating
            ? {
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: '#fff',
                '&:hover': { bgcolor: 'primary.dark' },
              }
            : {
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: `1px solid ${tokens.line}`,
                bgcolor: tokens.surface,
                color: tokens.muted,
                '&:hover': { bgcolor: tokens.hover, color: tokens.ink2 },
              },
          { '& svg': { fontSize: 20, transition: 'transform .4s cubic-bezier(0.4, 0, 0.2, 1)' }, '&:hover svg': { transform: 'rotate(-20deg)' } },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {dark ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Tooltip>
  );
}
